import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const International = () => {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedTerminals, setExpandedTerminals] = useState({
    T3: true,
    T2: true
  });

  // 日本時間フォーマット関数
  const formatJapanTime = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  // 既存データを取得する関数（スクレイピングなし）
  const fetchExistingData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 統合APIの正しいエンドポイント
      const apiUrl = 'https://haneda-international-api-746632636311.asia-northeast1.run.app/flights/international';
      console.log('国際線API URL:', apiUrl);
      console.log('フェッチ開始:', apiUrl);
      
      // タイムアウト付きfetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30秒タイムアウト
      
      const response = await fetch(apiUrl, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        cache: 'no-cache'
      });
      
      clearTimeout(timeoutId);
      console.log('レスポンス状態:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.status}`);
      }
      
      const data = await response.json().catch(() => []);
      console.log('取得データ件数:', data.length);
      
      // データ構造を変換
      const processedData = Array.isArray(data) ? data.map(item => ({
        id: item.id,
        status: item.status,
        scheduled_time: item.scheduled_time,
        changed_time: item.changed_time || '-',
        destination: item.airport || item.destination,
        airline: item.airline,
        flight_number: item.flight_number,
        terminal: item.terminal.toString().replace('T', ''),
        gate: item.gate,
        scrape_date: item.scrape_date
      })) : [];
      
      setFlights(processedData);
      // 既存データ取得時の更新日時を記録
      const now = new Date();
      console.log('International - Setting lastUpdated:', now);
      setLastUpdated(now);
    } catch (err) {
      console.error("Error fetching international data:", err);
      if (err.name === 'AbortError') {
        setError('リクエストがタイムアウトしました。ネットワーク接続を確認してください。');
      } else {
        setError(`Failed to fetch international data: ${err.message}`);
      }
      setFlights([]);
    } finally {
      setLoading(false);
    }
  };

  // スクレイピング実行とデータ更新を行う関数
  const updateData = async () => {
    setLoading(true);
    setError(null);
    try {
      // スクレイピング実行
      const scrapeResponse = await fetch('https://haneda-international-api-746632636311.asia-northeast1.run.app/scrape/international', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!scrapeResponse.ok) throw new Error('スクレイピングに失敗しました');
      
      // データベースから最新データを取得 (キャッシュ回避のためタイムスタンプを追加)
      const timestamp = new Date().getTime();
      const dataResponse = await fetch(`https://haneda-international-api-746632636311.asia-northeast1.run.app/flights/international?t=${timestamp}`);
      if (!dataResponse.ok) throw new Error('データ取得に失敗しました');
      
      const newData = await dataResponse.json();
      const processedData = Array.isArray(newData) ? newData.map(item => ({
        id: item.id,
        status: item.status,
        scheduled_time: item.scheduled_time,
        changed_time: item.changed_time || '-',
        destination: item.airport || item.destination,
        airline: item.airline,
        flight_number: item.flight_number,
        terminal: item.terminal.toString().replace('T', ''),
        gate: item.gate,
        scrape_date: item.scrape_date
      })) : [];
      setFlights(processedData);
      // 更新日時を記録
      const now = new Date();
      console.log('International - Update completed, setting lastUpdated:', now);
      setLastUpdated(now);
      alert('データが最新状態に更新されました');
    } catch (err) {
      setError(`データ更新に失敗: ${err.message}`);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 初回ロード時は既存データのみ取得（スクレイピングなし）
  useEffect(() => {
    fetchExistingData();
  }, []);

  // 共通ヘッダーコンポーネント
  const PageHeader = () => {
    console.log('International PageHeader rendering, lastUpdated:', lastUpdated);
    return (
      <header className="main-header">
        <div className="breadcrumb">
          <Link to="/home">ホーム</Link> {'>'} <span>国際線フライト情報</span>
        </div>
        <h1>羽田空港 国際線フライト情報</h1>
        {lastUpdated && (
          <div className="last-updated">
            最終更新: {formatJapanTime(lastUpdated)}
          </div>
        )}
        <nav className="main-nav">
          <Link to="/home">ホーム</Link>
          <Link to="/domestic">国内線</Link>
          <Link to="/international" className="active">国際線</Link>
        </nav>
      </header>
    );
  };

  // ローディング表示
  if (loading) return (
    <div className="app-container">
      <PageHeader />
      <div className="container" style={{ textAlign: 'center', padding: '50px' }}>
        <div className="loading">データを読み込み中...</div>
      </div>
    </div>
  );

  // エラー表示
  if (error) return (
    <div className="app-container">
      <PageHeader />
      <div className="container" style={{ textAlign: 'center', padding: '50px' }}>
        <div className="error">エラー: {error}</div>
      </div>
    </div>
  );

  // ターミナル別にフライトを分類
  const flightsByTerminal = {
    'T3': flights.filter(flight => flight.terminal === '3'),
    'T2': flights.filter(flight => flight.terminal === '2')
  };

  // 1時間毎のフライト数を計算
  const getHourlyFlights = (terminalFlights, terminal) => {
    const hourlyCounts = Array(24).fill(0).map((_, hour) => {
      const hourStr = hour.toString().padStart(2, '0');
      return terminalFlights.filter(flight => 
        flight.scheduled_time.startsWith(`${hourStr}:`)
      ).length;
    });

    const colors = terminal === 'T3' ? 
      { background: 'rgba(54, 162, 235, 0.6)', border: 'rgba(54, 162, 235, 1)' } : 
      { background: 'rgba(255, 99, 132, 0.6)', border: 'rgba(255, 99, 132, 1)' };

    return {
      labels: Array(24).fill().map((_, i) => `${i}:00`),
      datasets: [{
        label: `フライト数 (${terminalFlights.length}便)`,
        data: hourlyCounts,
        backgroundColor: colors.background,
        borderColor: colors.border,
        borderWidth: 1
      }]
    };
  };

  // ターミナルセクションを表示
  const renderTerminalSection = (terminal) => {
    const terminalData = flightsByTerminal[terminal];
    const isExpanded = expandedTerminals[terminal];
    const terminalName = terminal === 'T3' ? 'ターミナル 3' : 'ターミナル 2';

    return (
      <div className="station-section" key={terminal}>
        <h3 onClick={() => setExpandedTerminals({...expandedTerminals, [terminal]: !isExpanded})}>
          {terminalName} ({terminalData.length}便)
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
            {isExpanded ? '▼' : '▶'}
          </span>
        </h3>
        
        <div className="chart-container" style={{ height: '300px', marginBottom: '20px' }}>
          <Bar 
            data={getHourlyFlights(terminalData, terminal)} 
            options={{
              scales: { y: { beginAtZero: true } },
              maintainAspectRatio: false
            }} 
          />
        </div>

        {isExpanded && (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>ステータス</th>
                  <th>予定時刻</th>
                  <th>変更時刻</th>
                  <th>行先</th>
                  <th>便名</th>
                  <th>ゲート</th>
                </tr>
              </thead>
              <tbody>
                {terminalData.map((flight, index) => (
                  <tr key={flight.id || index}>
                    <td>{flight.status || '-'}</td>
                    <td>{flight.scheduled_time || '-'}</td>
                    <td>{flight.changed_time || '-'}</td>
                    <td>{flight.destination || '-'}</td>
                    <td>{flight.flight_number || '-'}</td>
                    <td>{flight.gate || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="app-container">
      <PageHeader />
      <div className="container">
        <div className="action-bar">
          <h2>国際線フライト情報</h2>
          <div className="action-buttons">
            <button 
              onClick={updateData}
              className="action-button update"
              disabled={loading}
            >
              {loading ? 'データ更新中...' : 'データ更新'}
            </button>
          </div>
        </div>

        {flights.length === 0 ? (
          <p>表示できる国際線フライトデータがありません。</p>
        ) : (
          <>
            {/* T3を優先表示 */}
            {renderTerminalSection('T3')}
            {renderTerminalSection('T2')}
          </>
        )}
      </div>
    </div>
  );
};

export default International;
